// YTRepliesCard — the locked add-on card that lives at the bottom of the
// Workbench (channelSlot="channel1") and Refinery (channelSlot="channel2")
// tabs. Renders the locked pitch + Unlock button + "How does this work? →"
// link that opens YTRepliesSetupModal. No OAuth wiring yet — visual bless
// round only. (Randy, Jul 2026 — per FEATURE_YT_AI_REPLIES_SPEC.md)
import { useState, useEffect } from "react";
import { YoutubeLogo, LockKey, ArrowRight, ChatCircleText, Robot, Link as LinkIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import YTRepliesSetupModal from "./YTRepliesSetupModal";
import { useEntitlements } from "@/lib/entitlements";
import { useCreatorProGate } from "@/components/CreatorProGateProvider";
import { api } from "@/lib/api";
import { useSkuPrice } from "@/lib/useSkuPrice";
import { fmt$ } from "@/lib/usePublicPricing";
import { useTrialKeepPrompt } from "@/lib/trial";

const SLOT_LABELS = {
  channel1: { name: "Channel 1", where: "Workbench" },
  channel2: { name: "Channel 2", where: "Refinery" },
};

export default function YTRepliesCard({ channelSlot = "channel1", siloId = null }) {
  useTrialKeepPrompt("yt_ai_replies");
  const [setupOpen, setSetupOpen] = useState(false);
  // Oct 3 2026 — the price was typed in as "$50". It is `day1_cents` on the
  // `yt_ai_replies` catalog node, editable in Super Admin → Creator Pro, so
  // it is read live now and shows nothing numeric until it arrives.
  const skuCents = useSkuPrice("yt_ai_replies");
  // Feb 2026 (Randy pt-yt-collapse-default) — YT Replies cards default to
  // COLLAPSED platform-wide (safeguard: no accidental engagement with a
  // module you haven't set up). Persisted per-user, per-silo via /me/ui-prefs
  // so once the user opens or closes it, that choice sticks across sessions,
  // browsers, and devices.
  const prefKey = `yt_replies_card_${channelSlot}_collapsed`;
  const [collapsed, setCollapsed] = useState(true);
  const [prefLoaded, setPrefLoaded] = useState(false);
  useEffect(() => {
    if (!siloId) { setPrefLoaded(true); return; }
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/me/ui-prefs", { params: { silo_id: siloId } });
        if (!cancel) {
          const v = (r.data?.prefs || {})[prefKey];
          setCollapsed(v === false ? false : true);
        }
      } catch { /* default stays collapsed */ }
      finally { if (!cancel) setPrefLoaded(true); }
    })();
    return () => { cancel = true; };
  }, [siloId, prefKey]);
  const toggleCollapsed = async () => {
    const next = !collapsed;
    setCollapsed(next);
    if (!siloId) return;
    // pref value represents the collapsed state directly (matches prefKey suffix).
    try { await api.patch("/me/ui-prefs", { silo_id: siloId, key: prefKey, value: next }); } catch { /* silent — visual already flipped */ }
  };
  const slot = SLOT_LABELS[channelSlot] || SLOT_LABELS.channel1;
  const { owns } = useEntitlements();
  const { openGate } = useCreatorProGate();
  const isUnlocked = owns("yt_ai_replies");

  const onUnlockClick = () => {
    // Jul 2026 — opens the Creator Pro buffet with yt_ai_replies preselected.
    // Requires + auto-apply logic on the backend cascades youtube_twin +
    // macro_autobuild + min_workbench so the buffet lands with the whole
    // family already checked.
    openGate("yt_ai_replies");
  };

  const onManageClick = () => {
    toast.info("Once you connect your channel, this becomes 'Open Chat Inbox' — for now, scroll to the console below.");
  };

  return (
    <>
      {/* "Add-ons" divider — signals this is a separate module, not another workbench step */}
      <div className="mt-8 mb-3 flex items-center gap-3" data-testid={`yt-replies-addons-divider-${channelSlot}`}>
        <div className="flex-1 h-px bg-[#1C1B1A]/10" />
        <span className="text-[9px] uppercase tracking-[0.28em] text-[#6B6A68] font-medium">Add-ons</span>
        <div className="flex-1 h-px bg-[#1C1B1A]/10" />
      </div>

      <div
        className="border hairline rounded-2xl bg-[#FDFCF8] shadow-sm overflow-hidden relative"
        data-testid={`yt-replies-card-${channelSlot}`}
      >
        {/* NEW ribbon (only while locked) OR Unlocked badge */}
        <div className="absolute top-0 right-0 z-10">
          {isUnlocked ? (
            <div className="bg-[#2F6B3A] text-white text-[9px] uppercase tracking-[0.24em] font-semibold px-3 py-1 rounded-bl-md">Unlocked</div>
          ) : (
            <div className="bg-[#C16E5A] text-white text-[9px] uppercase tracking-[0.24em] font-semibold px-3 py-1 rounded-bl-md">New</div>
          )}
        </div>

        {/* Card header with lock + chevron. Feb 17, 2026 (Randy pt-add-on-badge-clip) — the "Unlocked"/"New" badge sits absolutely-positioned at top-right (z-10) and used to visually clip the "Expand ▾" chevron. Reserve right-padding so the button clears the badge. */}
        <div className="px-5 py-3 pr-28 border-b hairline flex items-center justify-between gap-3 bg-white/40">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex items-center gap-1 shrink-0">
              <YoutubeLogo size={16} weight="fill" className="text-[#B54848]" />
              {isUnlocked
                ? <span className="w-2 h-2 rounded-full bg-[#2F6B3A]" title="Unlocked" />
                : <LockKey size={13} weight="duotone" className="text-[#6B6A68]" />}
            </div>
            <span className="text-[10px] uppercase tracking-[0.24em] text-[#6B6A68] font-medium">Add-on · YouTube Integration · {slot.name}</span>
          </div>
          <button
            onClick={toggleCollapsed}
            data-testid={`yt-replies-collapse-${channelSlot}`}
            className="text-[10px] uppercase tracking-[0.16em] text-[#4A5D4E] hover:text-[#1C1B1A] px-2 py-1"
            aria-label={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? "Expand ▾" : "Collapse ▴"}
          </button>
        </div>

        {!collapsed && (
          <div className="p-5 sm:p-6 space-y-5">
            {/* THE WORK AROUND — leads before the pitch. Oct 2026 (Randy):
                *"Call it a work around… make it clear that it's a work around
                so users can still direct their audience to the public Q&A."*
                It used to tell a $15 creator to build their twin "with the
                macro system", which is a Pro tool padlocked on this very
                screen — a promise the account cannot keep. */}
            <div className="bg-[#EAF3EC]/60 border border-[#B9D4BE] rounded-lg p-4">
              <div className="flex items-start gap-2.5">
                <LinkIcon size={16} weight="duotone" className="text-[#2F6B3A] mt-0.5 shrink-0" />
                <p className="text-[13px] text-[#2F6B3A] leading-relaxed">
                  <strong>The work around</strong> — if you'd rather put this purchase off for now,
                  you can still send your audience to your Silo. Put your <strong>public Silo link</strong> in
                  your video description and pin it in the comments. Fans click through and ask your
                  Silo anything, in your words. You answer nothing twice; it just happens on your
                  page instead of under your video.
                </p>
              </div>
            </div>

            <div className="border-t hairline pt-5 flex flex-col sm:flex-row gap-5 sm:gap-6">
              {/* Left ~55% — pitch */}
              <div className="flex-1 min-w-0 space-y-3">
                <h3 className="font-serif-display text-xl sm:text-2xl text-[#1C1B1A] leading-tight">
                  AI YouTube Comment Replies — {slot.name}
                </h3>
                <p className="text-[13.5px] text-[#3A3A38] leading-relaxed">
                  Prefer to have My Silo answer YouTube comments <strong>automatically</strong>, without your fans ever leaving YouTube? This is that.
                </p>
                <ul className="space-y-1.5 pt-1">
                  {[
                    { icon: Robot, text: "Auto-drafted replies from your Silo brain" },
                    { icon: ChatCircleText, text: "Preview inbox — you approve before anything posts" },
                    { icon: LinkIcon, text: "Every reply signed with a soft link back to your Silo" },
                  ].map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <li key={i} className="flex items-start gap-2 text-[13px] text-[#3A3A38]">
                        <span className="w-4 h-4 rounded-full bg-[#EAF3EC] text-[#2F6B3A] flex items-center justify-center shrink-0 mt-0.5">
                          <Icon size={10} weight="bold" />
                        </span>
                        <span>{f.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Right ~45% — YouTube comment mockup */}
              <div className="w-full sm:w-[280px] shrink-0">
                <div className="border hairline rounded-lg bg-white overflow-hidden shadow-sm">
                  {/* Fake YouTube comment */}
                  <div className="p-3 border-b hairline">
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#4A5D4E] text-white text-[10px] font-medium flex items-center justify-center shrink-0">JM</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10.5px] font-medium text-[#1C1B1A]">@jasonmiller · 2h</div>
                        <div className="text-[11.5px] text-[#3A3A38] mt-0.5 leading-snug">Which laptop would you recommend for a college freshman under $800?</div>
                      </div>
                    </div>
                  </div>
                  {/* AI reply from creator */}
                  <div className="p-3 bg-[#FDF4EE]/60">
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#B54848] text-white flex items-center justify-center shrink-0">
                        <YoutubeLogo size={13} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10.5px] font-medium text-[#1C1B1A] flex items-center gap-1">
                          Your Channel <span className="text-[9px] px-1 py-0.5 rounded bg-[#C16E5A] text-white uppercase tracking-[0.14em]">Creator</span>
                        </div>
                        <div className="text-[11.5px] text-[#3A3A38] mt-0.5 leading-snug">
                          Great question — I covered this exactly in my Aug review. Under $800, the Acer Swift 3 is the sweet spot. Full breakdown here: youtu.be/xyz
                        </div>
                        <div className="text-[9.5px] text-[#6B6A68] italic mt-1">— Answered by Randy&apos;s AI Silo · mysilo.app/randy</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* How does this work + Footer row */}
            <div className="border-t hairline pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                onClick={() => setSetupOpen(true)}
                data-testid={`yt-replies-how-it-works-${channelSlot}`}
                className="text-[13px] text-[#2C6E92] hover:text-[#1B4E6E] hover:underline font-medium inline-flex items-center gap-1 self-start sm:self-auto"
              >
                How does this work? <ArrowRight size={12} weight="bold" />
              </button>
              <div className="flex items-center gap-4">
                <div className="text-[13px] text-[#1C1B1A]">
                  <span className="font-serif-display text-xl" data-testid={`yt-replies-price-${channelSlot}`}>
                    {fmt$(skuCents)}
                  </span>
                  <span className="text-[11px] text-[#6B6A68] ml-1">one-time</span>
                </div>
                {isUnlocked ? (
                  <button
                    onClick={onManageClick}
                    data-testid={`yt-replies-manage-${channelSlot}`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] text-[13px] font-medium"
                  >
                    <ChatCircleText size={13} weight="duotone" /> Manage
                  </button>
                ) : (
                  <button
                    onClick={onUnlockClick}
                    data-testid={`yt-replies-unlock-${channelSlot}`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-[13px] font-medium"
                  >
                    <LockKey size={13} weight="duotone" /> Unlock
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <YTRepliesSetupModal open={setupOpen} onClose={() => setSetupOpen(false)} channelSlot={channelSlot} siloId={siloId} />
    </>
  );
}
